import React, { useState, useEffect } from 'react';

interface SettingsProps {
  onProcessedFileSelect: (filename: string) => void;
}

const Settings: React.FC<SettingsProps> = ({ onProcessedFileSelect }) => {
  const [file, setFile] = useState<File | null>(null);
  const [processedFiles, setProcessedFiles] = useState<string[]>([]);
  const [selectedProcessedFile, setSelectedProcessedFile] = useState<string | null>(null);

  // Fetch processed files on component mount
  useEffect(() => {
    fetchProcessedFiles();
  }, []);

  // Set initial selected file from localStorage or default to first
  useEffect(() => {
    if (processedFiles.length > 0) {
      const storedSelection = localStorage.getItem('selectedProcessedFile');
      if (storedSelection && processedFiles.includes(storedSelection)) {
        setSelectedProcessedFile(storedSelection);
        onProcessedFileSelect(storedSelection);
      } else {
        setSelectedProcessedFile(processedFiles[0]);
        onProcessedFileSelect(processedFiles[0]);
        localStorage.setItem('selectedProcessedFile', processedFiles[0]);
      }
    } else {
      setSelectedProcessedFile(null);
      onProcessedFileSelect(""); // Clear selection if no files
    }
  }, [processedFiles, onProcessedFileSelect]);

  const fetchProcessedFiles = async () => {
    try {
      const response = await fetch('/uploaded_processed_files/list');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setProcessedFiles(data.files);
    } catch (error) {
      console.error('Error fetching processed files:', error);
      alert('获取已处理文件列表失败。');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('请选择一个Excel文件。');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/upload_settings', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      alert(data.message);
      // Re-fetch the list of processed files after successful upload
      fetchProcessedFiles();
    } catch (error) {
      console.error('File upload error:', error);
      alert('文件上传失败。');
    }
  };

  const handleProcessedFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProcessedFile(event.target.value);
    onProcessedFileSelect(event.target.value);
    localStorage.setItem('selectedProcessedFile', event.target.value);
  };

  const handleDeleteNetwork = async () => {
    if (!selectedProcessedFile) {
      alert('请选择一个要删除的网络文件。');
      return;
    }
    const baseName = selectedProcessedFile.replace(/^s6output\./, '').replace(/\.csv$/, '');
    if (!window.confirm(`确定要删除网络文件组 (基于 "${baseName}") 吗？此操作不可撤销。`)) {
      return;
    }

    try {
      const response = await fetch(`/networks/delete_by_company_name/${encodeURIComponent(baseName)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      alert(`网络文件组 (基于 ${baseName}) 已成功删除。`);
      setSelectedProcessedFile(null);
      onProcessedFileSelect("");
      localStorage.removeItem('selectedProcessedFile');
      fetchProcessedFiles(); // Refresh the list
    } catch (error: unknown) {
      console.error("Delete network error:", error);
      if (error instanceof Error) {
        alert(`删除网络文件组 (基于 ${baseName}) 失败: ${error.message}`);
      } else {
        alert(`删除网络文件组 (基于 ${baseName}) 失败: ${String(error)}`);
      }
    }
  };

  return (
    <div>
      <h2>自家网络设置</h2>
      <p>上传一个Excel文件 (.xlsx)</p>
      <input type="file" accept=".xlsx" onChange={handleFileChange} />
      <button onClick={handleUpload}>上传并运行脚本</button>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' }}>
        <h3>已处理的自家网络文件 (.csv)</h3>
        {processedFiles.length > 0 ? (
          <>
            <form style={{ textAlign: 'left', width: 'fit-content' }}>
              {processedFiles.map((filename) => {
                const companyName = filename.replace(/^s6output\./, '').replace(/\.csv$/, '');
                return (
                  <div key={filename}>
                    <label>
                      <input
                        type="radio"
                        name="processed_network_file"
                        value={filename}
                        checked={selectedProcessedFile === filename}
                        onChange={handleProcessedFileChange}
                      />
                      {companyName}
                    </label>
                  </div>
                );
              })}
            </form>
            <button
              onClick={handleDeleteNetwork}
              disabled={!selectedProcessedFile}
              style={{ marginTop: '10px' }}
            >
              删除网络
            </button>
          </>
        ) : (
          <p>没有已处理的网络文件。</p>
        )}
      </div>
    </div>
  );
};

export default Settings;